import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  switchScan,
  take,
  tap,
} from 'rxjs/operators';
import { makeDepartmentPermissionsComplete } from 'server/helpers/permissions/permissions';
import { Department } from 'server/shared/models/department';
import { Organisation } from 'server/shared/models/organisation';
import { RoleEnum } from 'server/shared/models/role.enum';
import {
  DispoUser,
  PoolerUser,
  RequesterUser,
  User,
} from 'server/shared/models/user';
import { OrganisationService } from 'src/app/services/organisation.service';
import { UserService } from 'src/app/services/user.service';
import { rxFilterIsNotEmpty } from 'src/helpers/filterIsNotEmpty';
import {
  ClearSelectionEvent,
  convertSelectionByDepartmentToDTO,
  DepartmentChangesDto,
  DepartmentMultiSelectColumn,
  DepartmentMultiSelectColumnConfiguration,
  DepartmentMultiSelectMode,
  DepartmentSelectionEvent,
  SelectAllEvent,
  SelectDepartmentsEvent,
  SelectionByColumn,
  SelectionByColumnWithIntermediate,
  SelectionByDepartment,
  SelectionByOrganisation,
} from '../../../../../../server/shared/helpers/departmentMultiselect';
import { CustomerService } from '../../../services/customer.service';
import { TranslationService } from '../../../services/translation.service';
import { ConfirmationDialog } from '../../confirmation-dialog/confirmation.component';
import { SubscriptionService } from '../../subscription.service';

@Component({
  selector: 'app-department-multiselect',
  templateUrl: './department-multiselect.component.html',
  styleUrls: ['./department-multiselect.component.scss'],
})
export class DepartmentMultiselectComponent {
  i18n = this.translationService.getTranslations('profile');

  @Output() unsavedChanges = new EventEmitter<DepartmentChangesDto>();

  @Input() disabled: boolean = true;

  private modeInput$ = new BehaviorSubject<
    DepartmentMultiSelectMode | undefined
  >(undefined);
  mode$ = this.modeInput$.pipe(rxFilterIsNotEmpty(), distinctUntilChanged());
  @Input() set mode(mode: DepartmentMultiSelectMode | undefined) {
    this.modeInput$.next(mode);
  }
  get mode() {
    return this.modeInput$.value;
  }

  private profileUserInput$ = new BehaviorSubject<User | undefined>(undefined);
  profileUser$ = this.profileUserInput$.pipe(rxFilterIsNotEmpty());
  @Input() set profileUser(profileUser: User | undefined) {
    this.profileUserInput$.next(profileUser);
  }
  get profileUser() {
    return this.profileUserInput$.value;
  }

  searchValue$ = new BehaviorSubject<string>('');
  showOnlySelectedDepartmentsCheckboxState$ = new BehaviorSubject<boolean>(
    false,
  );

  organisations$ = this.organisationService.readyOrganisations$;
  departments$ = this.organisationService.readyDepartments$;

  filteredOrganisations$ = combineLatest([
    this.organisations$,
    this.showOnlySelectedDepartmentsCheckboxState$,
    this.searchValue$,
  ]).pipe(
    switchMap(([organisations, showOnlySelected, searchValue]) => {
      let filteredOrganisations = [...organisations];
      if (searchValue) {
        filteredOrganisations = this.filterOrganisationsBySearch(
          organisations,
          searchValue,
        );
      }
      if (showOnlySelected) {
        return this.combinedSelection$.pipe(
          map((selection) => {
            return this.filterOrganisationsBySelection(
              filteredOrganisations,
              selection,
            );
          }),
        );
      } else {
        return of(filteredOrganisations);
      }
    }),
  );

  filteredDepartments$ = this.filteredOrganisations$.pipe(
    map((organisations) =>
      organisations.flatMap((organisation) => organisation.departments),
    ),
  );

  filterOrganisationsBySelection(
    organisations: Organisation[],
    selection: SelectionByDepartment,
  ): Organisation[] {
    const filteredOrganisations = [...organisations];
    return filteredOrganisations
      .map((organisation) => {
        const filteredDepartments = organisation.departments.filter(
          (department) => {
            const departmentSelection: SelectionByColumn =
              selection.get(department.id) || new Map();
            return Array.from(departmentSelection.entries()).some(
              ([column, selected]) => selected,
            );
          },
        );
        return {
          ...organisation,
          departments: filteredDepartments,
        };
      })
      .filter((organisation) => organisation.departments.length > 0);
  }

  filterOrganisationsBySearch(
    organisations: Organisation[],
    searchValue: string,
  ): Organisation[] {
    const searchValues = searchValue.toLowerCase().split(' ');
    return organisations
      .map((organisation) => {
        const filteredDepartments = organisation.departments.filter(
          (department) => {
            return searchValues.every((searchValue) => {
              return (
                department.name.toLowerCase().includes(searchValue) ||
                organisation.name.toLowerCase().includes(searchValue)
              );
            });
          },
        );
        return {
          ...organisation,
          departments: filteredDepartments,
        };
      })
      .filter((organisation) => organisation.departments.length > 0);
  }

  // 'undefined' means full access
  editableDepartmentIds$: Observable<Set<string> | undefined> = this.mode$.pipe(
    switchMap((mode) => {
      switch (mode) {
        case 'pooler-assignment':
        case 'edit-read-write':
          return this.userService.dispoPermissions$.pipe(
            map((dispoPermissions) => {
              const departments = dispoPermissions.departments;
              if (departments.writeAll) {
                return undefined;
              }
              const writableDepartmentIds = Object.entries(
                dispoPermissions.departments.specificDepartmentIds,
              )
                .filter(([_, department]) => department.write)
                .map(([id]) => id);
              return new Set(writableDepartmentIds);
            }),
          );
        case 'edit-blacklist-inverted':
          return of(undefined);
      }
    }),
    shareReplay(1),
  );

  editableOrganisationIds$: Observable<Set<string> | undefined> = combineLatest(
    [this.editableDepartmentIds$, this.organisations$],
  ).pipe(
    map(([editableDepartmentIds, organisations]) => {
      if (editableDepartmentIds === undefined) {
        return undefined; // All organisations are editable
      }

      const editableOrganisationIds = new Set<string>();
      organisations.forEach((org) => {
        const allDepartmentsEditable = org.departments.every((dept) =>
          editableDepartmentIds.has(dept.id),
        );
        if (allDepartmentsEditable) {
          editableOrganisationIds.add(org._id);
        }
      });

      return editableOrganisationIds;
    }),
    shareReplay(1),
  );

  selectAllVisible$ = this.mode$.pipe(
    switchMap((mode) => {
      switch (mode) {
        case 'pooler-assignment':
        case 'edit-read-write':
          return this.userService.dispoPermissions$.pipe(
            map((dispoPermissions) => dispoPermissions.departments.writeAll),
          );
        case 'edit-blacklist-inverted':
          return of(false);
      }
    }),
  );
  allDepartmentsSelection$: Observable<SelectionByColumnWithIntermediate> =
    this.mode$.pipe(
      switchMap((mode) => {
        switch (mode) {
          case 'pooler-assignment':
          case 'edit-read-write':
            return combineLatest([
              this.selectAllVisible$,
              this.combinedSelection$,
              this.filteredDepartments$,
              this.activeColumns$,
            ]).pipe(
              map(
                ([
                  selectAllVisible,
                  combinedSelection,
                  departments,
                  activeColumns,
                ]) => {
                  if (!selectAllVisible) {
                    return new Map<
                      DepartmentMultiSelectColumn,
                      'selected' | 'indeterminate' | 'unselected'
                    >();
                  }

                  const allDepartmentsSelection = new Map<
                    DepartmentMultiSelectColumn,
                    'selected' | 'indeterminate' | 'unselected'
                  >();
                  activeColumns.forEach((column) => {
                    const departmentStates = departments.map((dept) => {
                      const deptSelection = combinedSelection.get(dept.id);
                      return deptSelection?.get(column) ?? false;
                    });
                    if (departmentStates.length === 0) {
                      allDepartmentsSelection.set(column, 'unselected');
                    } else if (
                      departmentStates.every((state) => state === true)
                    ) {
                      allDepartmentsSelection.set(column, 'selected');
                    } else if (
                      departmentStates.every((state) => state === false)
                    ) {
                      allDepartmentsSelection.set(column, 'unselected');
                    } else {
                      allDepartmentsSelection.set(column, 'indeterminate');
                    }
                  });

                  return allDepartmentsSelection;
                },
              ),
            );
          case 'edit-blacklist-inverted':
            return of(new Map());
        }
      }),
    );

  savedSelectionByDepartment$: Observable<SelectionByDepartment> =
    combineLatest([this.mode$, this.profileUser$, this.userService.role$]).pipe(
      switchMap(([mode, profileUser, currentUserRole]) => {
        switch (mode) {
          case 'pooler-assignment':
            if (profileUser.role !== RoleEnum.POOL) {
              throw new Error(
                'Can only use "pooler-assignment" mode profileUser of role Pooler',
              );
            }
            return this.customerService.readyDispoCustomer$.pipe(
              map((customer) =>
                this.getPoolerAssignmentSelection(profileUser, customer._id),
              ),
            );
          case 'edit-read-write':
            if (
              (profileUser.role !== RoleEnum.DISPO &&
                profileUser.role !== RoleEnum.REQUESTER) ||
              currentUserRole !== RoleEnum.DISPO
            ) {
              throw new Error(
                'Can only use "edit-read-write" mode with profileUser of role Dispo or Requester and as dispo',
              );
            }
            return this.departments$.pipe(
              map((departments) => {
                return this.getEditReadWriteSelectionForUser(
                  profileUser,
                  departments,
                );
              }),
            );
          case 'edit-blacklist-inverted':
            if (profileUser.role !== RoleEnum.POOL) {
              throw new Error(
                'Can only use "edit-inverted-blacklist" mode with profileUser of role Pooler',
              );
            }
            return this.departments$.pipe(
              map((departments) =>
                this.getInvertedBlacklistSelectionForUser(
                  profileUser,
                  departments,
                ),
              ),
            );
        }
      }),
      shareReplay(1),
    );

  getInvertedBlacklistSelectionForUser(
    pooler: PoolerUser,
    allDepartments: Department[],
  ): SelectionByDepartment {
    const selection: SelectionByDepartment = new Map();
    const blacklistedSet = new Set(pooler.userPreferenceDepartmentBlacklist);
    allDepartments.forEach((department) => {
      const departmentSelection = selection.get(department.id) || new Map();
      if (!blacklistedSet.has(department.id)) {
        departmentSelection.set('blacklist-inverted', true);
      }
      selection.set(department.id, departmentSelection);
    });
    return selection;
  }

  getEditReadWriteSelectionForUser(
    profileUser: DispoUser | RequesterUser,
    allDepartments: Department[],
  ): SelectionByDepartment {
    const selection: SelectionByDepartment = new Map();
    const departmentAccess =
      profileUser.role === RoleEnum.DISPO
        ? makeDepartmentPermissionsComplete(
            profileUser.dispoPermissions.departments,
          )
        : makeDepartmentPermissionsComplete(profileUser.departmentAccess);

    if (departmentAccess?.readAll || departmentAccess?.writeAll) {
      const allDepartmentIds = allDepartments.map(
        (department) => department.id,
      );
      if (departmentAccess?.readAll) {
        this.addIdsToSelection(selection, allDepartmentIds, 'read', true);
      }
      if (departmentAccess?.writeAll) {
        this.addIdsToSelection(selection, allDepartmentIds, 'write', true);
      }
      if (departmentAccess?.readAll && departmentAccess?.writeAll) {
        return selection;
      }
    }
    // apply specific department ids permissions
    Object.entries(departmentAccess?.specificDepartmentIds ?? {})?.forEach(
      ([departmentId, departmentAccess]) => {
        const departmentSelection = selection.get(departmentId) || new Map();
        if (departmentAccess.read) {
          departmentSelection.set('read', departmentAccess.read);
        }
        if (departmentAccess.write) {
          departmentSelection.set('write', departmentAccess.write);
        }
        selection.set(departmentId, departmentSelection);
      },
    );
    return selection;
  }

  addIdsToSelection(
    selection: SelectionByDepartment,
    departmentIds: string[],
    column: DepartmentMultiSelectColumn,
    value: boolean,
  ) {
    departmentIds.forEach((id) => {
      const departmentSelection = selection.get(id) || new Map();
      departmentSelection.set(column, value);
      selection.set(id, departmentSelection);
    });
  }

  getPoolerAssignmentSelection(
    profileUser: PoolerUser,
    customerId: string,
  ): SelectionByDepartment {
    const selection: SelectionByDepartment = new Map();
    const assignedDepartmentIds =
      profileUser.customerInformation?.[customerId]?.departmentIds;
    assignedDepartmentIds?.forEach((id) => {
      const departmentSelection = selection.get(id) || new Map();
      departmentSelection.set('pooler-assigned', true);
      selection.set(id, departmentSelection);
    });
    const autoBookDepartmentIds =
      profileUser.customerInformation?.[customerId]?.autoBookDepartmentIds ??
      [];
    this.addIdsToSelection(
      selection,
      autoBookDepartmentIds,
      'pooler-direct-accept',
      true,
    );
    return selection;
  }

  // Column configurations (static)
  columnConfigurations: Map<
    DepartmentMultiSelectColumn,
    DepartmentMultiSelectColumnConfiguration
  > = new Map([
    [
      'pooler-assigned',
      {
        icon: 'house-check',
        tooltipKey: 'departmentAccess',
      },
    ],
    [
      'pooler-direct-accept',
      {
        dependsOn: 'pooler-assigned',
        icon: 'auto-book-star',
        tooltipKey: 'enableAutoDirectAccept',
      },
    ],
    [
      'read',
      {
        icon: 'eye-outline',
        tooltipKey: 'read',
      },
    ],
    [
      'write',
      {
        dependsOn: 'read',
        icon: 'pencil-outline',
        tooltipKey: 'update',
      },
    ],
    [
      'blacklist-inverted',
      { icon: 'house-check', tooltipKey: 'departmentAccess' },
    ],
  ]);

  // Dependency maps (derived from columnConfigurations)
  columnDependsOn = Array.from(this.columnConfigurations.entries()).reduce(
    (map, [column, config]) => {
      if (config.dependsOn) {
        map.set(column, [config.dependsOn]);
      }
      return map;
    },
    new Map<DepartmentMultiSelectColumn, DepartmentMultiSelectColumn[]>(),
  );

  columnIsRequiredFor = Array.from(this.columnConfigurations.entries()).reduce(
    (map, [column, config]) => {
      if (config.dependsOn) {
        const requiredFor = map.get(config.dependsOn) || [];
        requiredFor.push(column);
        map.set(config.dependsOn, requiredFor);
      }
      return map;
    },
    new Map<DepartmentMultiSelectColumn, DepartmentMultiSelectColumn[]>(),
  );

  // Active columns based on mode
  activeColumns$: Observable<Set<DepartmentMultiSelectColumn>> =
    this.mode$.pipe(
      switchMap((mode: DepartmentMultiSelectMode) => {
        const activeColumns = new Set<DepartmentMultiSelectColumn>();
        switch (mode) {
          case 'pooler-assignment':
            activeColumns.add('pooler-assigned');
            return this.customerService.readyDispoCustomer$.pipe(
              map((customer) => {
                if (customer.enableAutoDirectAccept) {
                  activeColumns.add('pooler-direct-accept');
                }
                return activeColumns;
              }),
            );
          case 'edit-read-write':
            activeColumns.add('read');
            activeColumns.add('write');
            return of(activeColumns);
          case 'edit-blacklist-inverted':
            activeColumns.add('blacklist-inverted');
            return of(activeColumns);
        }
      }),
    );

  // Selection events and state management
  private selectionEvent$ = new Subject<DepartmentSelectionEvent>();

  // Scan selection events into unsaved changes state
  private unsavedChanges$: Observable<SelectionByDepartment> = combineLatest([
    this.selectionEvent$,
    this.savedSelectionByDepartment$,
  ]).pipe(
    switchScan(
      (
        unsavedChanges: SelectionByDepartment,
        [event, savedSelectionByDepartment],
      ) => {
        return this.applySelectionEventToUnsavedChanges(
          unsavedChanges,
          event,
        ).pipe(
          map((newState) =>
            this.removeUnsavedChangesThatEqualSavedState(
              newState,
              savedSelectionByDepartment,
            ),
          ),
        );
      },
      new Map<string, SelectionByColumn>(),
    ),
    startWith(new Map<string, SelectionByColumn>()),
    shareReplay(1),
  );

  applyChangedEvent(
    unsavedChanges: SelectionByDepartment,
    event: SelectDepartmentsEvent,
  ): SelectionByDepartment {
    const newUnsavedChanges = new Map(unsavedChanges);
    event.departmentIds.forEach((deptId) => {
      const departmentSelection = newUnsavedChanges.get(deptId) || new Map();
      event.columns.forEach((column) => {
        departmentSelection.set(column, event.enabled);
      });
      newUnsavedChanges.set(deptId, departmentSelection);
    });
    return newUnsavedChanges;
  }

  applySelectAllEvent(
    unsavedChanges: SelectionByDepartment,
    event: SelectAllEvent,
    departments: Department[],
  ): SelectionByDepartment {
    const newUnsavedChanges = new Map(unsavedChanges);
    departments.forEach((department) => {
      const departmentSelection =
        newUnsavedChanges.get(department.id) || new Map();
      event.columns.forEach((column) => {
        departmentSelection.set(column, event.enabled);
      });
      newUnsavedChanges.set(department.id, departmentSelection);
    });
    return newUnsavedChanges;
  }

  applySelectionEventToUnsavedChanges(
    state: SelectionByDepartment,
    event: DepartmentSelectionEvent,
  ): Observable<SelectionByDepartment> {
    const newState = new Map(state);
    switch (event.type) {
      case 'clear':
        newState.clear();
        return of(newState);
      case 'changed':
        return of(this.applyChangedEvent(newState, event));
      case 'select_all':
        return this.filteredDepartments$.pipe(
          take(1),
          map((departments) =>
            this.applySelectAllEvent(newState, event, departments),
          ),
        );
    }
  }

  removeUnsavedChangesThatEqualSavedState(
    unsavedChanges: SelectionByDepartment,
    savedSelectionByDepartment: SelectionByDepartment,
  ): SelectionByDepartment {
    const filteredChanges = new Map<string, SelectionByColumn>();

    for (const [departmentId, unsavedColumnSelection] of unsavedChanges) {
      const savedColumnSelection =
        savedSelectionByDepartment.get(departmentId) || new Map();
      const filteredColumnSelection = new Map<
        DepartmentMultiSelectColumn,
        boolean
      >();

      for (const [column, unsavedValue] of unsavedColumnSelection) {
        const savedValue = savedColumnSelection.get(column) ?? false;

        // Only keep unsaved changes that differ from saved state
        // Treat undefined as false for comparison
        if ((unsavedValue ?? false) !== savedValue) {
          filteredColumnSelection.set(column, unsavedValue);
        }
      }

      // Only add department to filtered changes if it has actual differences
      if (filteredColumnSelection.size > 0) {
        filteredChanges.set(departmentId, filteredColumnSelection);
      }
    }

    return filteredChanges;
  }

  // Combine original selection state with unsaved changes
  combinedSelection$: Observable<SelectionByDepartment> = combineLatest([
    this.savedSelectionByDepartment$,
    this.unsavedChanges$,
  ]).pipe(
    map(([originalSelection, unsavedChanges]) => {
      const combined = new Map(originalSelection);

      // Apply unsaved changes on top of original selection
      for (const [departmentId, changes] of unsavedChanges) {
        const originalDeptSelection = combined.get(departmentId) || new Map();
        const updatedDeptSelection = new Map(originalDeptSelection);

        // Apply each column change
        for (const [column, enabled] of changes) {
          updatedDeptSelection.set(column, enabled);
        }

        combined.set(departmentId, updatedDeptSelection);
      }

      return combined;
    }),
    shareReplay(1),
  );

  // Aggregate department selections by organisation
  combinedSelectionByOrganisation$: Observable<SelectionByOrganisation> =
    combineLatest([this.filteredOrganisations$, this.combinedSelection$]).pipe(
      map(([filteredOrganisations, combinedSelection]) => {
        const organisationSelection = new Map<
          string,
          SelectionByColumnWithIntermediate
        >();

        filteredOrganisations.forEach((org) => {
          const orgColumnStates = new Map<
            DepartmentMultiSelectColumn,
            'selected' | 'indeterminate' | 'unselected'
          >();

          // Get all columns that might be active
          const allColumns = Array.from(this.columnConfigurations.keys());

          allColumns.forEach((column) => {
            const departmentStates = org.departments.map((dept) => {
              const deptSelection = combinedSelection.get(dept.id);
              return deptSelection?.get(column) ?? false;
            });

            if (departmentStates.length === 0) {
              orgColumnStates.set(column, 'unselected');
            } else if (departmentStates.every((state) => state === true)) {
              orgColumnStates.set(column, 'selected');
            } else if (departmentStates.every((state) => state === false)) {
              orgColumnStates.set(column, 'unselected');
            } else {
              orgColumnStates.set(column, 'indeterminate');
            }
          });

          organisationSelection.set(org._id, orgColumnStates);
        });

        return organisationSelection;
      }),
      shareReplay(1),
    );

  // Effect to emit unsaved changes
  emitUnsavedChangesEffect$ = combineLatest([
    this.unsavedChanges$.pipe(skip(1)),
    this.mode$,
  ]).pipe(
    tap(([changes, mode]) => {
      const dto: DepartmentChangesDto = {
        mode,
        selection: convertSelectionByDepartmentToDTO(changes),
      };
      this.unsavedChanges.emit(dto);
    }),
  );

  dispoPermissions$ = this.userService.dispoPermissions$;

  blacklistedByPooler$: Observable<Set<string>> = combineLatest([
    this.mode$,
    this.profileUser$,
  ]).pipe(
    map(([mode, profileUser]) => {
      switch (mode) {
        case 'pooler-assignment':
          if (profileUser.role !== RoleEnum.POOL) {
            throw new Error(
              'Can only use "edit-pooler" mode with profileUser of role POOL',
            );
          }
          const poolerUser = profileUser as PoolerUser;
          return new Set(poolerUser.userPreferenceDepartmentBlacklist || []);
        case 'edit-read-write':
        case 'edit-blacklist-inverted':
          return new Set<string>();
      }
    }),
    shareReplay(1),
  );

  blacklistedByDepartment$: Observable<Set<string>> = combineLatest([
    this.mode$,
    this.profileUser$,
    this.departments$,
  ]).pipe(
    map(([mode, profileUser, departments]) => {
      switch (mode) {
        case 'pooler-assignment':
          if (profileUser.role !== RoleEnum.POOL) {
            throw new Error(
              'Can only use "edit-pooler" mode with profileUser of role POOL',
            );
          }
          const blacklistedDepartmentIds = new Set<string>();
          departments.forEach((dept) => {
            const isBlacklisted = dept.blacklistedUsers?.some(
              (blacklistedUser) => blacklistedUser.id === profileUser._id,
            );
            if (isBlacklisted) {
              blacklistedDepartmentIds.add(dept.id);
            }
          });
          return blacklistedDepartmentIds;
        case 'edit-read-write':
        case 'edit-blacklist-inverted':
          return new Set<string>();
      }
    }),
    shareReplay(1),
  );

  constructor(
    private translationService: TranslationService,
    private subscriptionService: SubscriptionService,
    private customerService: CustomerService,
    private organisationService: OrganisationService,
    private userService: UserService,
    public dialog: MatDialog,
  ) {
    // Initialize effects
    this.emitUnsavedChangesEffect$.subscribe();
  }

  trackByOrganisationId(index: number, organisation: Organisation): any {
    return organisation._id;
  }

  trackByDepartmentId(index: number, department: Department): any {
    return department.id;
  }

  // Helper method to get affected columns based on dependencies
  private getAffectedColumns(
    column: DepartmentMultiSelectColumn,
    enabled: boolean,
  ): DepartmentMultiSelectColumn[] {
    const columnsToToggle: DepartmentMultiSelectColumn[] = [column];

    if (enabled) {
      // If enabling, also enable columns this one depends on
      const dependsOn = this.columnDependsOn.get(column) || [];
      columnsToToggle.push(...dependsOn);
    } else {
      // If disabling, also disable columns that require this column to be enabled
      const requiredFor = this.columnIsRequiredFor.get(column) || [];
      columnsToToggle.push(...requiredFor);
    }

    return columnsToToggle;
  }

  // New reactive methods for column selection with dependency handling
  toggleDepartmentSelection(
    departmentId: string,
    column: DepartmentMultiSelectColumn,
    enabled: boolean,
  ) {
    const columnsToToggle = this.getAffectedColumns(column, enabled);

    const event: SelectDepartmentsEvent = {
      type: 'changed',
      departmentIds: [departmentId],
      enabled,
      columns: columnsToToggle,
    };
    this.selectionEvent$.next(event);
  }

  toggleSelectAll(column: DepartmentMultiSelectColumn, targetStaet: boolean) {
    const dialogContent = targetStaet
      ? {
          title: this.i18n.selectAllDepartments,
          text: this.i18n.areYouSureSelectAllDepartments,
        }
      : {
          title: this.i18n.deselectAllDepartments,
          text: this.i18n.areYouSureDeselectAllDepartments,
        };

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        ...dialogContent,
        id: `toggle-all-${column}-${targetStaet}`,
      },
    });

    dialogRef.afterClosed().subscribe((result: string) => {
      if (result) {
        const columnsToToggle = this.getAffectedColumns(column, targetStaet);

        const event: SelectAllEvent = {
          type: 'select_all',
          enabled: targetStaet,
          columns: columnsToToggle,
        };
        console.log({ event });

        this.selectionEvent$.next(event);
      }
    });
  }

  clearSelection() {
    const event: ClearSelectionEvent = {
      type: 'clear',
    };
    this.selectionEvent$.next(event);
  }

  // Organization-level selection (applies to all departments in the organization)
  toggleOrganizationColumn(
    organization: Organisation,
    column: DepartmentMultiSelectColumn,
    enabled: boolean,
  ) {
    const columnsToToggle = this.getAffectedColumns(column, enabled);
    if (organization) {
      const departmentIds = organization.departments.map((dept) => dept.id);
      const event: SelectDepartmentsEvent = {
        type: 'changed',
        departmentIds,
        enabled,
        columns: columnsToToggle,
      };
      console.log({ event });

      this.selectionEvent$.next(event);
    }
  }
}
